// Widget "Formularios pendientes de atención" del Dashboard — junta los
// QuestionnaireResponse en `status: 'in-progress'` de TODOS los pacientes
// del consultorio (el AccessPolicy del tenant ya acota el resultado, no
// hace falta filtrar por practitioner-autor: es una bandeja compartida,
// igual que Alertas clínicas/Pacientes del día). Reusa el mismo mecanismo
// de NotasTab.tsx: click en la fila reabre el formulario para completarlo
// (mismo link `/${patientId}/formulario/${codigo}?respuestaId=...` que deja
// guardar como "completed"), botón "Descartar" pasa el status a "stopped"
// (nunca se borra el recurso — mismo criterio que el resto del proyecto).
import { useMedplum } from '@medplum/react-hooks';
import { formatHumanName } from '@medplum/core';
import type { Patient, QuestionnaireResponse } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { InsigniaFormularioPropio } from '@/components/InsigniaFormularioPropio';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useTitulosFormulario } from '@/paciente/useTitulosFormulario';
import { claseTarjetaConPendientes, IndicadorPendiente } from './AlertaPendienteUI';

/**
 * Resuelve nombre por id de paciente — recibe los ids directo (no
 * QuestionnaireResponse) para que lo pueda reusar tanto la lista de
 * formularios pendientes como el detalle de completitud por paciente
 * (DetallePacienteHc más abajo) sin duplicar la búsqueda a Medplum.
 */
/** Exportada además para AlertasCompletitudPropias.tsx — mismo mecanismo id→nombre, sin duplicar la búsqueda a Medplum. */
export function usePacientesPorId(ids: readonly string[]): Map<string, string> {
  const medplum = useMedplum();
  const [mapa, setMapa] = useState<Map<string, string>>(new Map());

  const clave = ids.slice().sort().join(',');

  useEffect(() => {
    if (ids.length === 0) {
      setMapa(new Map());
      return;
    }
    let cancelado = false;
    medplum
      .search('Patient', { _id: ids.join(','), _count: ids.length })
      .then((bundle) => {
        if (cancelado) return;
        const nuevo = new Map<string, string>();
        for (const entry of bundle.entry ?? []) {
          const p = entry.resource as Patient | undefined;
          if (!p?.id) continue;
          nuevo.set(p.id, p.name?.[0] ? formatHumanName(p.name[0]) : p.id);
        }
        setMapa(nuevo);
      })
      .catch(() => {
        if (!cancelado) setMapa(new Map());
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `clave` ya resume todo lo que puede cambiar en `ids`
  }, [medplum, clave]);

  return mapa;
}

export function FormulariosPendientes(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [respuestas, setRespuestas] = useState<QuestionnaireResponse[] | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [descartando, setDescartando] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const r = await medplum.searchResources('QuestionnaireResponse', { status: 'in-progress', _sort: '-authored', _count: 50 });
      setRespuestas(r as QuestionnaireResponse[]);
    } catch {
      setRespuestas([]);
    }
  }, [medplum]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const lista = respuestas ?? [];
  const titulos = useTitulosFormulario(lista);
  const idsPacientes = lista.map((r) => r.subject?.reference).filter((ref): ref is string => !!ref).map((ref) => ref.replace('Patient/', ''));
  const pacientes = usePacientesPorId(idsPacientes);

  async function descartar(qr: QuestionnaireResponse): Promise<void> {
    if (!qr.id) return;
    setDescartando(qr.id);
    try {
      await medplum.updateResource<QuestionnaireResponse>({ ...qr, status: 'stopped' });
      await cargar();
    } finally {
      setDescartando(null);
    }
  }

  function continuar(qr: QuestionnaireResponse): void {
    const patientId = qr.subject?.reference?.replace('Patient/', '');
    const info = qr.questionnaire ? titulos.get(qr.questionnaire) : undefined;
    if (!patientId || !info?.editable) return;
    const qs = new URLSearchParams({ respuestaId: qr.id ?? '' });
    if (qr.encounter?.reference) qs.set('encounter', qr.encounter.reference);
    setAbierto(false);
    void navigate(`/${patientId}/formulario/${info.codigo}?${qs.toString()}`);
  }

  function filaDe(qr: QuestionnaireResponse): { paciente: string; formulario: string; editable: boolean; codigo?: string } {
    const info = qr.questionnaire ? titulos.get(qr.questionnaire) : undefined;
    const patientId = qr.subject?.reference?.replace('Patient/', '');
    return {
      paciente: (patientId && pacientes.get(patientId)) || qr.subject?.display || '—',
      formulario: info ? t(`hub.formulario.${info.codigo}`, info.titulo) : (qr.questionnaire ?? '—'),
      editable: info?.editable ?? false,
      codigo: info?.codigo,
    };
  }

  return (
    <div className={claseTarjetaConPendientes(lista.length > 0)}>
      <div className="flex items-center gap-1.5">
        {lista.length > 0 && <IndicadorPendiente />}
        <button type="button" onClick={() => setAbierto(true)} className="text-sm font-medium text-hce-ink hover:text-hce-pine hover:underline">
          {t('dashboard.formulariosPendientes.titulo')} ({lista.length})
        </button>
      </div>
      <p className="mt-0.5 text-xs text-hce-muted">{t('dashboard.formulariosPendientes.instruccion')}</p>

      <ul className="mt-2 space-y-1.5">
        {lista.length === 0 && <li className="text-xs text-hce-muted">{t('dashboard.formulariosPendientes.sinPendientes')}</li>}
        {lista.slice(0, 5).map((qr) => {
          const fila = filaDe(qr);
          return (
            <li key={qr.id} className="flex items-center gap-2">
              <button
                type="button"
                disabled={!fila.editable}
                onClick={() => continuar(qr)}
                className={`min-w-0 flex-1 truncate text-left text-sm ${fila.editable ? 'text-hce-ink hover:text-hce-pine hover:underline' : 'text-hce-muted'}`}
              >
                {fila.paciente} — {fila.formulario}
              </button>
              <Button variant="ghost" size="sm" disabled={descartando === qr.id} onClick={() => void descartar(qr)}>
                {descartando === qr.id ? t('dashboard.formulariosPendientes.descartando') : t('dashboard.formulariosPendientes.descartar')}
              </Button>
            </li>
          );
        })}
      </ul>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('dashboard.formulariosPendientes.titulo')}</DialogTitle>
          </DialogHeader>

          {lista.length === 0 ? (
            <p className="py-3 text-sm text-hce-muted">{t('dashboard.formulariosPendientes.sinPendientes')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('dashboard.formulariosPendientes.paciente')}</TableHead>
                  <TableHead>{t('dashboard.formulariosPendientes.formulario')}</TableHead>
                  <TableHead>{t('dashboard.formulariosPendientes.fecha')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((qr) => {
                  const fila = filaDe(qr);
                  return (
                    <TableRow key={qr.id}>
                      <TableCell className="text-hce-ink">{fila.paciente}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          {fila.editable ? (
                            <button type="button" onClick={() => continuar(qr)} className="text-hce-pine underline-offset-2 hover:underline">
                              {fila.formulario}
                            </button>
                          ) : (
                            fila.formulario
                          )}
                          {fila.codigo && <InsigniaFormularioPropio codigo={fila.codigo} />}
                        </div>
                      </TableCell>
                      <TableCell className="text-hce-muted">{qr.authored ? new Date(qr.authored).toLocaleString('es-EC') : '—'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" disabled={descartando === qr.id} onClick={() => void descartar(qr)}>
                          {descartando === qr.id ? t('dashboard.formulariosPendientes.descartando') : t('dashboard.formulariosPendientes.descartar')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
